import { useState } from 'react';
import { useLoaderData, useNavigate, useSearchParams } from 'react-router';
import type { LoaderFunctionArgs } from 'react-router';

import { Avatar, Badge, Button, Card, Icon, KpiCard } from '@ds/core';
import { DataTable, FilterBar } from '@ds/data';
import type { DataTableColumn, RowId } from '@ds/data';
import { EmptyState } from '@ds/feedback';
import { SearchField } from '@ds/forms';
import { Menu, Pagination, SegmentedControl } from '@ds/navigation';
import { ExportMenu } from '@/comuns/ExportMenu';
import { TODOS_OS_REGISTROS } from '@/comuns/exportar';
import { PageHeader } from '@/comuns/PageHeader';
import { dataSimples, diaIso, horaDe, mesEAno, moeda, segundaDaSemana, somarDias } from '@/comuns/formato';
import { dados } from '@/data/porta';
import type { AbaDeAtendimentos, Atendimento, CargaDeAtendimentos, PeriodoDeAtendimentos, SituacaoDoAtendimento } from '@/data/tipos';
import { diaDaUrl } from './Agenda';
import { AnotarComVecchiaModal } from './atendimento/AnotarComVecchiaModal';
import { microfoneSuportado } from './atendimento/gravadorEmPartes';
import { podeNaRota } from '@/router/guardas';
import { useRotaAtual } from '@/router/useRotaAtual';
import { pode } from '@/session/sessao';

const POR_PAGINA = 8;

const SITUACAO: Record<SituacaoDoAtendimento, { label: string; tom: 'success' | 'info' | 'warning' | 'danger' | 'neutral' }> = {
    'aguardando': { label: 'Aguardando', tom: 'warning' },
    'em-atendimento': { label: 'Em atendimento', tom: 'info' },
    'concluido': { label: 'Concluído', tom: 'success' },
    'cancelado': { label: 'Cancelado', tom: 'danger' },
    'faltou': { label: 'Faltou', tom: 'neutral' },
};

function abaDaRota(pathname: string): AbaDeAtendimentos {
    if (pathname.endsWith('/em-aberto')) return 'em-aberto';
    if (pathname.endsWith('/todos')) return 'todos';
    return 'hoje';
}

function periodoDaUrl(bruto: string | null, aba: AbaDeAtendimentos): PeriodoDeAtendimentos {
    if (bruto === 'dia' || bruto === 'semana' || bruto === 'mes') return bruto;
    /* "Hoje" abre no dia; as outras abas, no mês — abrir "Todos" num único dia
       mostraria três linhas e pareceria que não há histórico. */
    return aba === 'hoje' ? 'dia' : 'mes';
}

export async function carregarAtendimentos({ request }: LoaderFunctionArgs): Promise<CargaDeAtendimentos> {
    const url = new URL(request.url);
    const aba = abaDaRota(url.pathname);
    return dados().atendimentos({
        aba,
        periodo: periodoDaUrl(url.searchParams.get('periodo'), aba),
        referencia: diaDaUrl(url.searchParams.get('data')),
        busca: url.searchParams.get('q') ?? undefined,
        pagina: Number(url.searchParams.get('pagina') ?? '1') || 1,
        porPagina: POR_PAGINA,
    });
}

export function Atendimentos() {
    const carga = useLoaderData() as CargaDeAtendimentos;
    const [params, setParams] = useSearchParams();
    const navigate = useNavigate();
    const handle = useRotaAtual();
    const [selecionados, setSelecionados] = useState<RowId[]>([]);
    const [vista, setVista] = useState<'lista' | 'grade'>('lista');
    const [anotando, setAnotando] = useState(false);

    const aba = (handle?.subrota?.path ?? 'hoje') as AbaDeAtendimentos;
    const periodo = periodoDaUrl(params.get('periodo'), aba);
    const referencia = diaDaUrl(params.get('data'));
    const busca = params.get('q') ?? '';

    const trocarParam = (chave: string, valor: string | null) => {
        setParams((atuais) => {
            const proximos = new URLSearchParams(atuais);
            if (valor === null || valor === '') proximos.delete(chave);
            else proximos.set(chave, valor);
            if (chave !== 'pagina') proximos.delete('pagina');
            return proximos;
        }, { replace: true });
    };

    const rotuloDoPeriodo = (): string => {
        if (periodo === 'dia') return dataSimples(referencia);
        if (periodo === 'semana') {
            const inicio = segundaDaSemana(referencia);
            return `${dataSimples(inicio)} – ${dataSimples(somarDias(inicio, 5))}`;
        }
        return mesEAno(referencia);
    };

    const colunas: DataTableColumn<Atendimento>[] = [
        { key: 'inicioEm', header: 'Data', sortable: true, render: (r) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{dataSimples(r.inicioEm.slice(0, 10))} · {horaDe(r.inicioEm)}</span> },
        {
            key: 'paciente', header: 'Paciente', sortable: true,
            render: (r) => (
                <span style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                    <Avatar name={r.paciente} size="xs" />
                    <span style={{ minWidth: 0 }}>
                        <strong style={{ display: 'block', fontWeight: 600 }}>{r.paciente}</strong>
                        <span style={{ display: 'block', fontSize: 12, color: 'var(--text-tertiary)' }}>{r.procedimento}</span>
                    </span>
                </span>
            ),
        },
        { key: 'profissional', header: 'Profissional' },
        {
            /* A ficha é do ATENDIMENTO, não do paciente: dizer que a consulta de
               hoje já tem anamnese porque existe uma de julho é informação
               errada, não imprecisão. */
            key: 'fichas', header: 'Fichas de atendimento',
            render: (r) => (r.fichas.length === 0
                ? <span style={{ color: 'var(--text-tertiary)' }}>nenhuma preenchida</span>
                : <span title={r.fichas.join(', ')} style={{ display: 'block', maxWidth: 240, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.fichas.join(', ')}</span>),
        },
        { key: 'valorCentavos', header: 'Valor', align: 'right', render: (r) => (r.valorCentavos ? moeda(r.valorCentavos) : '—') },
        { key: 'situacao', header: 'Situação', render: (r) => <Badge tone={SITUACAO[r.situacao].tom} dot>{SITUACAO[r.situacao].label}</Badge> },
    ];

    const porDia = carga.itens.reduce<Record<string, Atendimento[]>>((acc, a) => {
        const dia = a.inicioEm.slice(0, 10);
        (acc[dia] ??= []).push(a);
        return acc;
    }, {});

    const abrir = (a: Atendimento) => navigate(`/app/atendimentos/editor/${a.id}`);
    const semFicha = carga.indicadores.find((i) => i.id === 'sem-ficha')?.valor;
    const primeiraSemFicha = carga.itens.find((a) => a.situacao === 'concluido' && a.fichas.length === 0);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <PageHeader
                title="Consultas"
                description="Registro clínico e operacional dos procedimentos realizados"
                actions={
                    <>
                        <ExportMenu<Atendimento>
                            label="Exportar" size="md"
                            carregarTudo={async () => (await dados().atendimentos({
                                aba, periodo, referencia, busca: busca || undefined, pagina: 1, porPagina: TODOS_OS_REGISTROS,
                            })).itens}
                            payload={{
                                title: 'Consultas', subtitle: `${carga.total} registros · ${rotuloDoPeriodo()}`,
                                rows: carga.itens,
                                columns: [
                                    { header: 'Data', value: (r) => `${dataSimples(r.inicioEm.slice(0, 10))} ${horaDe(r.inicioEm)}` },
                                    { header: 'Paciente', value: (r) => r.paciente },
                                    { header: 'Procedimento', value: (r) => r.procedimento },
                                    { header: 'Profissional', value: (r) => r.profissional },
                                    { header: 'Fichas', value: (r) => r.fichas.join(', ') },
                                    { header: 'Situação', value: (r) => SITUACAO[r.situacao].label },
                                    { header: 'Valor', value: (r) => moeda(r.valorCentavos) },
                                ],
                            }}
                        />
                        {/* Os modelos de ficha moram numa rota irmã. Quem procura
                            "ficha de atendimento" procura aqui, no módulo — não
                            adianta a tela existir sem porta de entrada. */}
                        {podeNaRota('fichas.editar') && (
                            <Button
                                variant="secondary" iconLeft={<Icon name="clipboard-pen" size={15} />}
                                onClick={() => navigate('/app/atendimentos/fichas')}
                            >
                                Fichas
                            </Button>
                        )}
                        {/* A consulta presencial que começa pela escuta: quem atende escolhe o
                            paciente e a VecchIA toma nota (0157). */}
                        {pode('atendimentos') && microfoneSuportado() && (
                            <Button variant="secondary" iconLeft={<Icon name="mic" size={15} />} onClick={() => setAnotando(true)}>
                                Anotar com VecchIA
                            </Button>
                        )}
                        <Button iconLeft={<Icon name="plus" size={15} />} onClick={() => navigate('/app/agenda?modal=novo')}>Registrar consulta</Button>
                    </>
                }
            >
                <SegmentedControl
                    value={aba} ariaLabel="Recorte de consultas"
                    onChange={(id) => navigate({ pathname: `/app/atendimentos/${id}`, search: params.toString() ? `?${params}` : '' })}
                    options={[
                        { value: 'hoje', label: `Hoje (${carga.totais.hoje})` },
                        { value: 'em-aberto', label: `Em aberto (${carga.totais.emAberto})` },
                        { value: 'todos', label: `Todos (${carga.totais.todos})` },
                    ]}
                />
            </PageHeader>

            <div className="vec-stagger" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 12 }}>
                {carga.indicadores.map((i) => (
                    <KpiCard key={i.id} label={i.rotulo} value={i.valor} delta={i.variacao} deltaTone={i.tendencia} hint={i.apoio} icon={<Icon name={i.icone} size={16} />} />
                ))}
            </div>

            {/* Era uma "sugestão da IA" que oferecia montar rascunhos e só mostrava
                um aviso. Não há IA nisso: é a contagem, e o caminho até a consulta. */}
            {semFicha !== undefined && semFicha !== '0' && (
                <div
                    role="status"
                    style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '10px 12px', background: 'var(--surface-secondary)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', fontSize: 13, color: 'var(--text-body)' }}
                >
                    <Icon name="clipboard-pen" size={15} style={{ color: 'var(--neutral-600)' }} />
                    <span style={{ flex: 1, minWidth: 0 }}>
                        {semFicha} {semFicha === '1' ? 'consulta concluída está' : 'consultas concluídas estão'} sem ficha preenchida.
                    </span>
                    {primeiraSemFicha && (
                        <Button size="sm" variant="secondary" onClick={() => abrir(primeiraSemFicha)}>
                            Preencher a de {primeiraSemFicha.paciente}
                        </Button>
                    )}
                </div>
            )}

            {/* Recorte de tempo e forma de exibição juntos: um é "quanto eu
                vejo", o outro é "como eu vejo", e a pessoa decide os dois de uma
                vez. A aba cuida da SITUAÇÃO — antes os dois recortavam a data e
                trocar de período não surtia efeito. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <SegmentedControl
                    value={periodo} onChange={(v) => trocarParam('periodo', v)} ariaLabel="Período"
                    options={[{ value: 'dia', label: 'Dia' }, { value: 'semana', label: 'Semana' }, { value: 'mes', label: 'Mês' }]}
                />
                <span style={{ fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)' }}>{rotuloDoPeriodo()}</span>
                <Button variant="ghost" size="sm" onClick={() => trocarParam('data', null)} disabled={referencia === diaIso()}>Hoje</Button>
                <span style={{ marginLeft: 'auto' }} />
                <SegmentedControl
                    value={vista} onChange={(v) => setVista(v as 'lista' | 'grade')} size="sm" ariaLabel="Forma de exibição"
                    options={[
                        { value: 'lista', label: 'Lista', icon: <Icon name="list" size={14} /> },
                        { value: 'grade', label: 'Grade', icon: <Icon name="layout-grid" size={14} /> },
                    ]}
                />
            </div>

            <FilterBar
                search={<SearchField value={busca} onChange={(v) => trocarParam('q', v)} placeholder="Buscar por paciente ou procedimento" width={260} />}
                resultLabel={`${carga.total} ${carga.total === 1 ? 'consulta' : 'consultas'}`}
                actions={selecionados.length > 0 ? (
                    <Menu
                        align="right" width={300}
                        onSelect={(id) => {
                            if (id === 'limpar') { setSelecionados([]); return; }
                            if (id === 'prontuario') {
                                const escolhida = carga.itens.find((a) => a.id === String(selecionados[0]));
                                if (escolhida) abrir(escolhida);
                            }
                        }}
                        trigger={<Button size="sm" variant="secondary" iconLeft={<Icon name="ellipsis" size={14} />}>Ações ({selecionados.length})</Button>}
                        /* As duas diziam "Ação em lote registrada" e não faziam nada.
                           Prontuário se preenche dentro da consulta, uma de cada vez;
                           cobrança nasce do orçamento aprovado em Vendas. */
                        items={[
                            selecionados.length === 1
                                ? { id: 'prontuario', label: 'Preencher prontuário', icon: 'clipboard-pen' }
                                : { id: 'prontuario', label: 'Preencher prontuário', icon: 'clipboard-pen', disabled: true, hint: 'uma consulta por vez' },
                            { id: 'faturar', label: 'Faturar selecionados', icon: 'receipt', disabled: true, hint: 'a cobrança nasce em Vendas' },
                            { id: 'divisor', label: '', divider: true },
                            { id: 'limpar', label: 'Limpar seleção', icon: 'x' },
                        ]}
                    />
                ) : null}
            />

            {vista === 'lista' ? (
                <>
                    <DataTable
                        selectable selected={selecionados} onSelect={setSelecionados} rowKey="id"
                        columns={colunas} rows={carga.itens} onRowClick={abrir}
                        /* A primeira coluna é a data: sem rótulo próprio, quem
                           ouve a tabela receberia o instante ISO no lugar do
                           nome do paciente. */
                        rowLabel={(a) => `${a.paciente} · ${a.procedimento}`}
                        emptyMessage="Nenhuma consulta no período."
                    />
                    <Pagination page={carga.pagina} pageCount={carga.paginas} total={carga.total} pageSize={POR_PAGINA} onChange={(p) => trocarParam('pagina', String(p))} />
                </>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                    {carga.itens.length === 0 && (
                        <Card padding={0}>
                            <EmptyState compact icon="calendar-x" title="Nenhuma consulta no período" description="Troque o recorte para semana ou mês, ou limpe a busca." />
                        </Card>
                    )}
                    {Object.entries(porDia).map(([dia, itens]) => (
                        <div key={dia} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            <h2 style={{ margin: 0, fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' }}>
                                {dataSimples(dia)} · {itens.length} {itens.length === 1 ? 'consulta' : 'consultas'}
                            </h2>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(min(280px,100%),1fr))', gap: 12 }}>
                                {itens.map((a) => (
                                    <Card
                                        key={a.id} padding={16} interactive onClick={() => abrir(a)}
                                        ariaLabel={`Abrir consulta de ${a.paciente}`}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                            <Avatar name={a.paciente} size="sm" />
                                            <span style={{ flex: 1, minWidth: 0 }}>
                                                <strong style={{ display: 'block', fontSize: 13.5 }}>{a.paciente}</strong>
                                                <span style={{ display: 'block', fontSize: 12, color: 'var(--text-tertiary)' }}>{horaDe(a.inicioEm)} · {a.procedimento}</span>
                                            </span>
                                            <Badge tone={SITUACAO[a.situacao].tom} dot>{SITUACAO[a.situacao].label}</Badge>
                                        </div>
                                        <p style={{ margin: '10px 0 0', fontSize: 12.5, color: 'var(--text-secondary)' }}>
                                            {a.fichas.length ? a.fichas.join(', ') : 'nenhuma ficha preenchida'}
                                        </p>
                                    </Card>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <AnotarComVecchiaModal
                open={anotando}
                onClose={() => setAnotando(false)}
                onAberta={(id, pedido) => {
                    setAnotando(false);
                    navigate(`/app/atendimentos/editor/${id}`, { state: { anotarComVecchia: pedido } });
                }}
            />
        </div>
    );
}
